import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ECOSYSTEM, KIND_LABEL, KIND_NOUN, type EcosystemItem, type ItemKind } from "@/lib/ecosystem";
import { SPOKES, spokeForItem, getSpoke, KIND_GLYPH, type SpokeId } from "@/lib/eightfold";
import { suggestActivationForHour } from "@/lib/activations";
import { absoluteUrl } from "@/lib/site";

export const Route = createFileRoute("/constellation")({
  head: () => ({
    meta: [
      { title: "Living Constellation · KAfrequency" },
      { name: "description", content: "Every book, song, teaching, and activation of KAfrequency as one living universe — navigated through the Eightfold Path." },
      { property: "og:title", content: "Living Constellation · KAfrequency" },
      { property: "og:description", content: "A living universe of KAfrequency — books, music, teachings, and activations arranged around the Eightfold Path." },
      { property: "og:url", content: absoluteUrl("/constellation") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Living Constellation · KAfrequency" },
      { name: "twitter:description", content: "A living universe of KAfrequency knowledge." },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/constellation") }],
  }),
  component: ConstellationPage,
});

// ---------- Visited persistence ----------
const VISITED_KEY = "ka.constellation.visited.v1";
function readVisited(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(VISITED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
}
function writeVisited(set: Set<string>) {
  try { window.localStorage.setItem(VISITED_KEY, JSON.stringify([...set])); } catch { /* noop */ }
}

// ---------- Layout math ----------
interface Node {
  key: string;
  item: EcosystemItem;
  spoke: SpokeId;
  x: number;
  y: number;
  r: number;
  ring: number;      // 0..2, distance ring index
  angleDeg: number;  // for aria
}

const VIEW = 1200;
const CENTER = VIEW / 2;
const WHEEL_R = 130;
const RING_RADII = [270, 380, 490];

function computeNodes(items: EcosystemItem[], focus: SpokeId | null): Node[] {
  // Group by spoke
  const groups: Record<SpokeId, EcosystemItem[]> = {
    view: [], intention: [], speech: [], action: [], livelihood: [], effort: [], mindfulness: [], concentration: [],
  };
  for (const item of items) groups[spokeForItem(item)].push(item);

  const nodes: Node[] = [];
  for (const spoke of SPOKES) {
    const arr = groups[spoke.id];
    const isFocus = focus === spoke.id;
    // In focus, the group fans wide (110°) toward the top; otherwise narrow (55°) around its natural angle.
    const spread = isFocus ? 110 : 55;
    // When focused, this spoke rotates to the top (angle 0). We rotate the whole layout so the focus lands at top later.
    const baseAngle = spoke.angle;
    const count = arr.length;
    arr.forEach((item, i) => {
      const t = count === 1 ? 0 : i / (count - 1) - 0.5;
      const angleDeg = baseAngle + t * spread;
      const ring = i % RING_RADII.length;
      const r = RING_RADII[ring] + (isFocus ? -20 : 0);
      const rad = ((angleDeg - 90) * Math.PI) / 180; // 0deg = top
      nodes.push({
        key: `${item.kind}:${item.slug}`,
        item,
        spoke: spoke.id,
        x: CENTER + Math.cos(rad) * r,
        y: CENTER + Math.sin(rad) * r,
        r: 6 + (2 - ring) * 1.5,
        ring,
        angleDeg,
      });
    });
  }
  return nodes;
}

// ---------- Component ----------
const KIND_FILTERS: readonly (ItemKind | "all")[] = ["all", "book", "music", "wisdom", "activation"];

function ConstellationPage() {
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [focus, setFocus] = useState<SpokeId | null>(null);
  const [kindFilter, setKindFilter] = useState<ItemKind | "all">("all");
  const [query, setQuery] = useState("");
  const [hovered, setHovered] = useState<string | null>(null);
  const [selected, setSelected] = useState<Node | null>(null);
  const [visited, setVisited] = useState<Set<string>>(new Set());
  const [reduced, setReduced] = useState(false);

  // Pan/zoom/rotate (SVG viewBox transform)
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0); // degrees, applied to the world group
  const dragRef = useRef<{ x: number; y: number; px: number; py: number } | null>(null);


  useEffect(() => {
    setVisited(readVisited());
    if (typeof window !== "undefined") {
      setReduced(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);
    }
  }, []);

  // Today's activation (soft-pulsing star)
  const todaySlug = useMemo(() => {
    if (typeof window === "undefined") return null;
    try { return suggestActivationForHour(new Date().getHours()).slug; } catch { return null; }
  }, []);

  // Filter dataset
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ECOSYSTEM.filter((it) => {
      if (kindFilter !== "all" && it.kind !== kindFilter) return false;
      if (!q) return true;
      const hay = `${it.title} ${it.subtitle} ${it.tags.join(" ")} ${it.body ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [kindFilter, query]);

  const nodes = useMemo(() => computeNodes(filteredItems, focus), [filteredItems, focus]);

  const matchesQuery = useCallback((n: Node) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const hay = `${n.item.title} ${n.item.subtitle} ${n.item.tags.join(" ")}`.toLowerCase();
    return hay.includes(q);
  }, [query]);

  // Rotation transform when a spoke is focused so it lands at the top
  const wheelRotation = focus ? -getSpoke(focus).angle : 0;

  const openNode = useCallback((n: Node) => {
    setVisited((prev) => {
      const next = new Set(prev);
      next.add(n.key);
      writeVisited(next);
      return next;
    });
    // Small delay for the ripple, unless reduced motion
    const go = () => navigate({ to: n.item.hrefRoute as never, params: { slug: n.item.slug } as never });
    if (reduced) go();
    else window.setTimeout(go, 120);
  }, [navigate, reduced]);

  // Pan handlers
  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if ((e.target as Element).closest("[data-node], [data-spoke]")) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { x: pan.x, y: pan.y, px: e.clientX, py: e.clientY };
  };
  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!dragRef.current) return;
    const dx = (e.clientX - dragRef.current.px) / zoom;
    const dy = (e.clientY - dragRef.current.py) / zoom;
    setPan({ x: dragRef.current.x + dx, y: dragRef.current.y + dy });
  };
  const onPointerUp = () => { dragRef.current = null; };
  const onWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    const next = Math.min(2.4, Math.max(0.5, zoom * (e.deltaY < 0 ? 1.1 : 0.9)));
    setZoom(next);
  };
  const zoomTo = (z: number) => setZoom(Math.min(2.4, Math.max(0.5, z)));
  const rotateBy = (deg: number) => setRotation((r) => ((r + deg) % 360 + 360) % 360);
  const panBy = (dx: number, dy: number) => setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); setRotation(0); setFocus(null); };

  // Keyboard: shortcuts for navigation, zoom, pan, rotate, reset.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ignore when typing in an input/textarea/search field.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      const step = 40 / zoom;
      switch (e.key) {
        case "Escape": setSelected(null); setFocus(null); break;
        case "+": case "=": zoomTo(zoom * 1.15); break;
        case "-": case "_": zoomTo(zoom * 0.87); break;
        case "0": resetView(); break;
        case "ArrowUp":    e.preventDefault(); panBy(0,  step); break;
        case "ArrowDown":  e.preventDefault(); panBy(0, -step); break;
        case "ArrowLeft":  e.preventDefault(); panBy( step, 0); break;
        case "ArrowRight": e.preventDefault(); panBy(-step, 0); break;
        case "[": rotateBy(-15); break;
        case "]": rotateBy(15); break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);


  const spokeCounts = useMemo(() => {
    const counts: Record<SpokeId, number> = {
      view: 0, intention: 0, speech: 0, action: 0, livelihood: 0, effort: 0, mindfulness: 0, concentration: 0,
    };
    for (const it of ECOSYSTEM) counts[spokeForItem(it)]++;
    return counts;
  }, []);

  return (
    <div className="relative">
      {/* Header */}
      <section className="mx-auto max-w-6xl px-4 pt-12 pb-6 sm:px-6 sm:pt-16 text-center" data-reveal="up">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">The Field · Living Universe</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl md:text-6xl text-gold-luminous">
          Living Constellation
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
          Every book, song, teaching, and activation as one connected body — navigated through the Eightfold Path.
          Drag or <kbd className="rounded border hairline px-1.5 py-0.5 text-[10px]">↑↓←→</kbd> to pan · scroll or
          <kbd className="ml-1 rounded border hairline px-1.5 py-0.5 text-[10px]">+ / −</kbd> to zoom ·
          <kbd className="ml-1 rounded border hairline px-1.5 py-0.5 text-[10px]">[ / ]</kbd> to rotate ·
          <kbd className="ml-1 rounded border hairline px-1.5 py-0.5 text-[10px]">0</kbd> to reset.
        </p>

      </section>

      {/* Controls */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6" data-reveal="up">
        <div className="flex flex-wrap items-center justify-center gap-2">
          {KIND_FILTERS.map((k) => (
            <button
              key={k}
              onClick={() => setKindFilter(k)}
              className={`rounded-full border px-3 py-1.5 text-xs uppercase tracking-[0.2em] transition-colors ${
                kindFilter === k
                  ? "border-gold bg-gold text-obsidian"
                  : "hairline text-muted-foreground hover:border-gold hover:text-gold"
              }`}
            >
              {k === "all" ? "All" : KIND_LABEL[k]}
            </button>
          ))}
        </div>
        <div className="mx-auto mt-4 flex max-w-xl items-center gap-2">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the constellation…"
            aria-label="Search the constellation"
            className="w-full rounded-full border hairline bg-charcoal/70 px-4 py-2.5 text-sm text-ivory placeholder:text-muted-foreground focus:border-gold focus:outline-none"
          />
          <button
            type="button"
            onClick={resetView}
            className="rounded-full border hairline px-3 py-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:border-gold hover:text-gold"
            aria-label="Reset view"
          >
            Reset
          </button>
        </div>
      </section>

      {/* Constellation canvas */}
      <section className="relative mt-6" data-reveal="up">
        <div
          ref={containerRef}
          className="constellation-shell relative mx-auto w-full max-w-6xl overflow-hidden rounded-2xl border hairline bg-obsidian"
          style={{ aspectRatio: "1 / 1", maxHeight: "min(86vh, 900px)" }}
        >
          {/* nebula backdrop */}
          <div className="constellation-nebula" aria-hidden />

          <svg
            ref={svgRef}
            viewBox={`0 0 ${VIEW} ${VIEW}`}
            role="img"
            aria-label="KAfrequency Living Constellation — an interactive universe of books, music, teachings, and activations arranged around the Eightfold Path."
            className="relative z-[1] block h-full w-full touch-none select-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onWheel={onWheel}
          >
            <defs>
              <radialGradient id="star-gold" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="var(--gold-luminous)" stopOpacity="1" />
                <stop offset="60%" stopColor="var(--gold)" stopOpacity="0.7" />
                <stop offset="100%" stopColor="var(--gold)" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="core-halo" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="var(--gold-luminous)" stopOpacity="0.55" />
                <stop offset="60%" stopColor="var(--gold)" stopOpacity="0.15" />
                <stop offset="100%" stopColor="var(--gold)" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="spoke-line" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="var(--gold)" stopOpacity="0.55" />
                <stop offset="100%" stopColor="var(--gold)" stopOpacity="0" />
              </linearGradient>
            </defs>

            <g
              transform={`translate(${pan.x} ${pan.y}) rotate(${rotation} ${CENTER} ${CENTER}) scale(${zoom})`}
              style={{ transformOrigin: `${CENTER}px ${CENTER}px`, transition: reduced ? undefined : "transform 240ms cubic-bezier(0.22, 1, 0.36, 1)" }}
            >

              {/* Distant particles */}
              <g aria-hidden="true" opacity="0.7">
                {PARTICLES.map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r={p.r} fill="var(--ivory)" fillOpacity={p.o} />
                ))}
              </g>

              {/* Guide rings */}
              <g aria-hidden="true" className="text-gold">
                <circle cx={CENTER} cy={CENTER} r={RING_RADII[2]} fill="none" stroke="currentColor" strokeOpacity="0.06" />
                <circle cx={CENTER} cy={CENTER} r={RING_RADII[1]} fill="none" stroke="currentColor" strokeOpacity="0.05" />
                <circle cx={CENTER} cy={CENTER} r={RING_RADII[0]} fill="none" stroke="currentColor" strokeOpacity="0.05" />
              </g>

              {/* Rotating Bodhi wheel */}
              <g
                className={reduced ? "" : "constellation-wheel-spin"}
                style={{ transformOrigin: `${CENTER}px ${CENTER}px`, transform: `rotate(${wheelRotation}deg)`, transition: "transform 1200ms cubic-bezier(0.22, 1, 0.36, 1)" }}
              >
                <g style={{ transformOrigin: `${CENTER}px ${CENTER}px` }}>
                  {/* Outer wheel ring */}
                  <circle cx={CENTER} cy={CENTER} r={WHEEL_R} fill="none" stroke="var(--gold)" strokeOpacity="0.55" strokeWidth="1.2" />
                  <circle cx={CENTER} cy={CENTER} r={WHEEL_R - 14} fill="none" stroke="var(--gold-luminous)" strokeOpacity="0.35" strokeWidth="0.8" />
                  <circle cx={CENTER} cy={CENTER} r={WHEEL_R - 40} fill="none" stroke="var(--gold)" strokeOpacity="0.2" strokeWidth="0.6" />

                  {/* 8 wheel spokes (visual only, above rings) */}
                  {SPOKES.map((s) => {
                    const rad = ((s.angle - 90) * Math.PI) / 180;
                    return (
                      <line
                        key={`ws-${s.id}`}
                        x1={CENTER}
                        y1={CENTER}
                        x2={CENTER + Math.cos(rad) * WHEEL_R}
                        y2={CENTER + Math.sin(rad) * WHEEL_R}
                        stroke="var(--gold)"
                        strokeOpacity={focus === s.id ? 0.9 : 0.35}
                        strokeWidth={focus === s.id ? 1.4 : 0.8}
                      />
                    );
                  })}
                </g>

                {/* KAfrequency core symbol — Ankh-inspired glyph */}
                <g style={{ transform: `rotate(${-wheelRotation}deg)`, transformOrigin: `${CENTER}px ${CENTER}px`, transition: "transform 1200ms cubic-bezier(0.22, 1, 0.36, 1)" }}>
                  <circle cx={CENTER} cy={CENTER} r={64} fill="url(#core-halo)" className={reduced ? "" : "constellation-core-breath"} />
                  <g stroke="var(--gold-luminous)" strokeWidth="2" fill="none" strokeLinecap="round">
                    <circle cx={CENTER} cy={CENTER - 20} r="18" strokeOpacity="0.95" />
                    <line x1={CENTER} y1={CENTER - 2} x2={CENTER} y2={CENTER + 40} strokeOpacity="0.9" />
                    <line x1={CENTER - 20} y1={CENTER + 10} x2={CENTER + 20} y2={CENTER + 10} strokeOpacity="0.9" />
                  </g>
                  <text x={CENTER} y={CENTER + 62} textAnchor="middle" fill="var(--gold-luminous)" style={{ font: "500 10px var(--font-sans)", letterSpacing: "0.35em" }}>
                    KAFREQUENCY
                  </text>
                </g>
              </g>

              {/* Long spoke reach-out lines (from wheel edge to ring radius) */}
              {SPOKES.map((s) => {
                const rad = ((s.angle - 90) * Math.PI) / 180;
                const x1 = CENTER + Math.cos(rad) * (WHEEL_R + 4);
                const y1 = CENTER + Math.sin(rad) * (WHEEL_R + 4);
                const x2 = CENTER + Math.cos(rad) * (RING_RADII[2] + 40);
                const y2 = CENTER + Math.sin(rad) * (RING_RADII[2] + 40);
                const active = focus === s.id;
                return (
                  <g key={`spoke-${s.id}`}>
                    <line
                      x1={x1} y1={y1} x2={x2} y2={y2}
                      stroke="var(--gold)"
                      strokeOpacity={active ? 0.5 : 0.12}
                      strokeWidth={active ? 1.2 : 0.6}
                    />
                    {/* Spoke label — clickable */}
                    <g
                      data-spoke
                      transform={`translate(${x2} ${y2})`}
                      style={{ cursor: "pointer" }}
                      onClick={() => setFocus((f) => (f === s.id ? null : s.id))}
                      role="button"
                      tabIndex={0}
                      aria-pressed={active}
                      aria-label={`${s.name} — ${spokeCounts[s.id]} items. ${s.essence}`}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setFocus((f) => (f === s.id ? null : s.id)); } }}
                    >
                      <circle r="26" fill="var(--obsidian)" fillOpacity="0.9" stroke="var(--gold)" strokeOpacity={active ? 0.9 : 0.4} strokeWidth={active ? 1.3 : 0.8} />
                      <text textAnchor="middle" y="-3" fill="var(--gold-luminous)" style={{ font: "600 10px var(--font-sans)", letterSpacing: "0.15em", textTransform: "uppercase" }}>
                        {s.short}
                      </text>
                      <text textAnchor="middle" y="10" fill="var(--gold)" style={{ font: "400 9px var(--font-sans)", letterSpacing: "0.1em" }}>
                        {spokeCounts[s.id]}
                      </text>
                    </g>
                  </g>
                );
              })}

              {/* Constellation lines from each node back to its spoke on the wheel */}
              <g aria-hidden="true">
                {nodes.map((n) => {
                  const s = getSpoke(n.spoke);
                  const rad = ((s.angle - 90) * Math.PI) / 180;
                  const wx = CENTER + Math.cos(rad) * (WHEEL_R + 4);
                  const wy = CENTER + Math.sin(rad) * (WHEEL_R + 4);
                  const active = focus === n.spoke || hovered === n.key;
                  const dim = query.trim() && !matchesQuery(n);
                  return (
                    <line
                      key={`ln-${n.key}`}
                      x1={wx} y1={wy} x2={n.x} y2={n.y}
                      stroke="var(--gold)"
                      strokeOpacity={dim ? 0.03 : active ? 0.4 : 0.09}
                      strokeWidth={active ? 0.9 : 0.5}
                    />
                  );
                })}
              </g>

              {/* Stars */}
              <g>
                {nodes.map((n) => {
                  const dim = (focus && focus !== n.spoke) || (query.trim() && !matchesQuery(n));
                  const isVisited = visited.has(n.key);
                  const isToday = n.item.kind === "activation" && n.item.slug === todaySlug;
                  const isHover = hovered === n.key;
                  return (
                    <g
                      key={n.key}
                      data-node
                      transform={`translate(${n.x} ${n.y})`}
                      opacity={dim ? 0.22 : 1}
                      style={{ cursor: "pointer", transition: reduced ? undefined : "opacity 300ms ease" }}
                      onPointerEnter={() => setHovered(n.key)}
                      onPointerLeave={() => setHovered((h) => (h === n.key ? null : h))}
                      onFocus={() => setHovered(n.key)}
                      onBlur={() => setHovered((h) => (h === n.key ? null : h))}
                      onClick={() => setSelected(n)}
                    >
                      <title>{`${KIND_NOUN[n.item.kind]} · ${n.item.title}`}</title>
                      {/* Halo */}
                      <circle
                        r={n.r * 3.2}
                        fill="url(#star-gold)"
                        opacity={isHover ? 0.9 : isToday ? 0.7 : isVisited ? 0.55 : 0.35}
                        className={isToday && !reduced ? "constellation-pulse" : undefined}
                      />
                      {/* Core */}
                      <circle
                        r={isHover ? n.r + 2 : n.r}
                        fill={hueFill(n.item.kind, isVisited)}
                        stroke="var(--gold-luminous)"
                        strokeOpacity={isHover || isVisited ? 0.9 : 0.5}
                        strokeWidth={isHover ? 1.4 : 0.9}
                      />
                      {/* Accessible focusable overlay */}
                      <a
                        aria-label={`${KIND_NOUN[n.item.kind]} — ${n.item.title}. ${n.item.subtitle}. Open details.`}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(n); } }}
                      >
                        <circle r={Math.max(14, n.r + 8)} fill="transparent" />
                      </a>
                    </g>
                  );
                })}
              </g>
            </g>
          </svg>

          {/* Accessible control panel — pan, zoom, rotate, reset + keyboard help */}
          <div
            role="group"
            aria-label="Constellation view controls"
            className="pointer-events-auto absolute bottom-3 right-3 z-[2] flex flex-col items-end gap-2"
          >
            <div className="flex items-center gap-1 rounded-full border hairline bg-obsidian/85 p-1 backdrop-blur">
              <button type="button" onClick={() => panBy(0, 40 / zoom)} aria-label="Pan up" className="inline-flex h-11 w-11 items-center justify-center rounded-full text-gold hover:bg-charcoal hover:text-gold-luminous focus:outline-none focus-visible:ring-1 focus-visible:ring-gold">↑</button>
              <button type="button" onClick={() => panBy(40 / zoom, 0)} aria-label="Pan left" className="inline-flex h-11 w-11 items-center justify-center rounded-full text-gold hover:bg-charcoal hover:text-gold-luminous focus:outline-none focus-visible:ring-1 focus-visible:ring-gold">←</button>
              <button type="button" onClick={() => panBy(-40 / zoom, 0)} aria-label="Pan right" className="inline-flex h-11 w-11 items-center justify-center rounded-full text-gold hover:bg-charcoal hover:text-gold-luminous focus:outline-none focus-visible:ring-1 focus-visible:ring-gold">→</button>
              <button type="button" onClick={() => panBy(0, -40 / zoom)} aria-label="Pan down" className="inline-flex h-11 w-11 items-center justify-center rounded-full text-gold hover:bg-charcoal hover:text-gold-luminous focus:outline-none focus-visible:ring-1 focus-visible:ring-gold">↓</button>
            </div>
            <div className="flex flex-wrap items-center gap-1 rounded-full border hairline bg-obsidian/85 p-1 backdrop-blur">
              <button type="button" onClick={() => zoomTo(zoom * 1.15)} aria-label="Zoom in" className="inline-flex h-11 w-11 items-center justify-center rounded-full text-gold hover:bg-charcoal hover:text-gold-luminous focus:outline-none focus-visible:ring-1 focus-visible:ring-gold">＋</button>
              <span aria-live="polite" aria-atomic="true" className="min-w-[3ch] px-1 text-center text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{Math.round(zoom * 100)}%</span>
              <button type="button" onClick={() => zoomTo(zoom * 0.87)} aria-label="Zoom out" className="inline-flex h-11 w-11 items-center justify-center rounded-full text-gold hover:bg-charcoal hover:text-gold-luminous focus:outline-none focus-visible:ring-1 focus-visible:ring-gold">－</button>
              <span className="mx-1 h-5 w-px bg-border" aria-hidden />
              <button type="button" onClick={() => rotateBy(-15)} aria-label="Rotate counter-clockwise" className="inline-flex h-11 w-11 items-center justify-center rounded-full text-gold hover:bg-charcoal hover:text-gold-luminous focus:outline-none focus-visible:ring-1 focus-visible:ring-gold">↺</button>
              <span aria-live="polite" aria-atomic="true" className="min-w-[3ch] px-1 text-center text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{Math.round(rotation)}°</span>
              <button type="button" onClick={() => rotateBy(15)} aria-label="Rotate clockwise" className="inline-flex h-11 w-11 items-center justify-center rounded-full text-gold hover:bg-charcoal hover:text-gold-luminous focus:outline-none focus-visible:ring-1 focus-visible:ring-gold">↻</button>
              <span className="mx-1 h-5 w-px bg-border" aria-hidden />
              <button type="button" onClick={resetView} aria-label="Reset view (zoom, pan, rotation, and pathway focus)" className="inline-flex h-11 min-w-11 items-center justify-center rounded-full px-3 text-[10px] uppercase tracking-[0.2em] text-gold hover:bg-charcoal hover:text-gold-luminous focus:outline-none focus-visible:ring-1 focus-visible:ring-gold">Reset</button>
            </div>
            <details className="w-full max-w-xs rounded-lg border hairline bg-obsidian/85 backdrop-blur">
              <summary className="cursor-pointer list-none rounded-lg px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-gold focus:outline-none focus-visible:ring-1 focus-visible:ring-gold">
                Keyboard shortcuts
              </summary>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 px-3 pb-3 text-[11px] text-muted-foreground">
                <dt><kbd className="rounded border hairline px-1.5 py-0.5 text-[10px] text-gold">↑ ↓ ← →</kbd></dt><dd>Pan the view</dd>
                <dt><kbd className="rounded border hairline px-1.5 py-0.5 text-[10px] text-gold">+</kbd> / <kbd className="rounded border hairline px-1.5 py-0.5 text-[10px] text-gold">−</kbd></dt><dd>Zoom in / out</dd>
                <dt><kbd className="rounded border hairline px-1.5 py-0.5 text-[10px] text-gold">[</kbd> / <kbd className="rounded border hairline px-1.5 py-0.5 text-[10px] text-gold">]</kbd></dt><dd>Rotate the field</dd>
                <dt><kbd className="rounded border hairline px-1.5 py-0.5 text-[10px] text-gold">0</kbd></dt><dd>Reset view</dd>
                <dt><kbd className="rounded border hairline px-1.5 py-0.5 text-[10px] text-gold">Esc</kbd></dt><dd>Clear pathway / close details</dd>
                <dt><kbd className="rounded border hairline px-1.5 py-0.5 text-[10px] text-gold">Tab</kbd></dt><dd>Move focus between stars & spokes</dd>
                <dt><kbd className="rounded border hairline px-1.5 py-0.5 text-[10px] text-gold">Enter</kbd></dt><dd>Open focused star or pathway</dd>
              </dl>
            </details>
          </div>

          {/* Focus badge */}
          {focus && (
            <div className="pointer-events-none absolute left-3 top-3 z-[2] rounded-full border-gold border bg-obsidian/85 px-4 py-1.5 text-xs uppercase tracking-[0.3em] text-gold-luminous backdrop-blur">
              {getSpoke(focus).name}
            </div>
          )}

          {/* Live status for screen readers */}
          <div role="status" aria-live="polite" className="sr-only">
            {focus ? `${getSpoke(focus).name} pathway selected.` : "All pathways visible."} {nodes.length} stars showing. Zoom {Math.round(zoom * 100)} percent. Rotation {Math.round(rotation)} degrees.
          </div>

        </div>

        {/* Legend */}
        <div className="mx-auto mt-4 flex max-w-6xl flex-wrap items-center justify-center gap-x-5 gap-y-2 px-4 text-[11px] uppercase tracking-[0.2em] text-muted-foreground sm:px-6">
          <LegendDot label="Book" kind="book" />
          <LegendDot label="Music" kind="music" />
          <LegendDot label="Teaching" kind="wisdom" />
          <LegendDot label="Activation" kind="activation" />
          <span className="text-gold">◐ Visited</span>
          <span className="text-gold-luminous">☀ Today's activation</span>
        </div>
      </section>

      {/* Eightfold pathway list — accessible fallback + deep-link */}
      <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6" data-reveal="up">
        <h2 className="text-center font-serif text-3xl text-gold-luminous">The Eightfold Path</h2>
        <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-muted-foreground">
          Each spoke is a category of practice — a way in, not a claim of arrival.
        </p>
        <ol className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {SPOKES.map((s, i) => {
            const active = focus === s.id;
            return (
              <li key={s.id}>
                <button
                  onClick={() => setFocus((f) => (f === s.id ? null : s.id))}
                  className={`card-luxe w-full rounded-xl border p-4 text-left ${active ? "border-gold" : "hairline"}`}
                >
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{String(i + 1).padStart(2, "0")} · Path</p>
                  <p className="mt-1.5 font-serif text-lg text-gold-luminous">{s.name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{s.essence}</p>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-gold">{spokeCounts[s.id]} stars</p>
                </button>
              </li>
            );
          })}
        </ol>
      </section>

      {/* Node detail drawer */}
      {selected && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${KIND_NOUN[selected.item.kind]} — ${selected.item.title}`}
          className="fixed inset-0 z-[80] flex items-end justify-center bg-obsidian/70 backdrop-blur-sm sm:items-center"
          onClick={() => setSelected(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-t-2xl border-gold border bg-charcoal p-6 sm:rounded-2xl animate-rise"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
              {KIND_NOUN[selected.item.kind]} · {getSpoke(selected.spoke).name}
            </p>
            <h3 className="mt-2 font-serif text-2xl text-gold-luminous">{selected.item.title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{selected.item.subtitle}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => { openNode(selected); setSelected(null); }}
                className="rounded-full bg-gold-gradient px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-obsidian"
              >
                Enter →
              </button>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:border-gold hover:text-gold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick links out */}
      <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6" data-reveal="up">
        <div className="rounded-2xl border hairline p-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Continue</p>
          <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
            The constellation is a map. The path is the daily practice.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link to="/activations" className="rounded-full border-gold border px-4 py-2 text-xs uppercase tracking-[0.2em] text-gold-luminous hover-lift">Daily Activations</Link>
            <Link to="/library" className="rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold hover:text-gold">Library</Link>
            <Link to="/map" className="rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold hover:text-gold">Knowledge Map</Link>
            <Link to="/timeline" className="rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold hover:text-gold">Sacred Timeline</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function hueFill(kind: ItemKind, visited: boolean): string {
  if (visited) return "var(--gold-luminous)";
  switch (kind) {
    case "book": return "var(--gold)";
    case "music": return "var(--ivory)";
    case "wisdom": return "var(--gold-luminous)";
    case "activation": return "var(--emerald)";
  }
}

function LegendDot({ label, kind }: { label: string; kind: ItemKind }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2 w-2 rounded-full" style={{ background: hueFill(kind, false) }} aria-hidden />
      <span>{label} <span className="text-gold/70">{KIND_GLYPH[kind]}</span></span>
    </span>
  );
}

// Deterministic starfield particles (no runtime randomness — SSR-stable).
const PARTICLES = (() => {
  const arr: { x: number; y: number; r: number; o: number }[] = [];
  let seed = 7;
  const rand = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
  for (let i = 0; i < 90; i++) {
    arr.push({
      x: rand() * VIEW,
      y: rand() * VIEW,
      r: 0.4 + rand() * 1.1,
      o: 0.15 + rand() * 0.35,
    });
  }
  return arr;
})();
